import React, { useState } from "react";
import { ThemeConfig, getThemeValue } from "../hooks/useVoiceWidget";
import { getStrings, Language } from "../i18n/strings";
import { NEUTRAL_PALETTE } from "../utils/neutralPalette";
import { HeyAiSendIcon } from "./HeyAiIcons";

interface ChatInputProps {
  onSendMessage: (text: string) => void | Promise<void>;
  onUserActivity?: () => void;
  theme?: Partial<ThemeConfig>;
  uiVariant?: "legacy" | "split-switch";
  language?: Language;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onUserActivity,
  theme,
  uiVariant = "legacy",
  language,
}) => {
  const t = getStrings(language);
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const useNeutralTextWindow = uiVariant === "split-switch";
  const focusBorderColor = useNeutralTextWindow
    ? NEUTRAL_PALETTE.slate
    : getThemeValue(theme, "primaryColor");
  const inactiveBorderColor = useNeutralTextWindow ? NEUTRAL_PALETTE.mist : "#d1d5db";
  const activeSendColor = useNeutralTextWindow
    ? NEUTRAL_PALETTE.anthracite
    : getThemeValue(theme, "primaryColor");
  const inactiveSendColor = useNeutralTextWindow ? NEUTRAL_PALETTE.mist : "#9ca3af";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim());
      setInputValue("");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={useNeutralTextWindow ? "heyai-panel-section heyai-panel-section--input" : undefined}
      style={{
        padding: useNeutralTextWindow ? "14px 86px 16px 16px" : "16px 20px",
        borderTop: useNeutralTextWindow
          ? "1px solid rgba(255,255,255,0.36)"
          : "1px solid #e2e8f0",
        background: useNeutralTextWindow
          ? "rgba(255,255,255,0.28)"
          : getThemeValue(theme, "chatBackground"),
      }}
    >
      <div
        style={{
          display: "flex",
          gap: useNeutralTextWindow ? "8px" : "12px",
          alignItems: "center",
          background: useNeutralTextWindow ? "rgba(255,255,255,0.50)" : "transparent",
          border: useNeutralTextWindow
            ? `1px solid ${isFocused ? "rgba(94,93,89,0.30)" : "rgba(255,255,255,0.62)"}`
            : "none",
          borderRadius: useNeutralTextWindow ? "18px" : undefined,
          padding: useNeutralTextWindow ? "8px 9px 8px 14px" : 0,
          boxShadow: useNeutralTextWindow
            ? "inset 0 1px 0 rgba(255,255,255,0.68), 0 6px 18px rgba(10,18,32,0.08)"
            : undefined,
        }}
      >
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onUserActivity?.();
          }}
          placeholder={t.inputPlaceholder}
          style={{
            flex: 1,
            minWidth: 0,
            padding: useNeutralTextWindow ? "8px 2px" : "12px 16px",
            border: useNeutralTextWindow
              ? "none"
              : `1px solid ${isFocused ? focusBorderColor : inactiveBorderColor}`,
            borderRadius: useNeutralTextWindow ? 0 : "25px",
            fontSize: useNeutralTextWindow ? "14px" : getThemeValue(theme, "fontSize"),
            outline: "none",
            backgroundColor: useNeutralTextWindow ? "transparent" : "#ffffff",
            color: "#1f2937",
            fontFamily: getThemeValue(theme, "fontFamily"),
          }}
          onFocus={() => {
            setIsFocused(true);
            onUserActivity?.();
          }}
          onBlur={() => setIsFocused(false)}
        />
        <button
          type="submit"
          disabled={!inputValue.trim()}
          aria-label={t.buttonSend}
          style={{
            background: inputValue.trim()
              ? activeSendColor
              : inactiveSendColor,
            border: "none",
            borderRadius: useNeutralTextWindow ? "14px" : "20px",
            minWidth: useNeutralTextWindow ? "40px" : "88px",
            width: useNeutralTextWindow ? "40px" : undefined,
            height: useNeutralTextWindow ? "40px" : "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            padding: "0 14px",
            color: inputValue.trim() || !useNeutralTextWindow
              ? "white"
              : NEUTRAL_PALETTE.anthracite,
            cursor: inputValue.trim() ? "pointer" : "not-allowed",
            transition: "all 0.2s ease",
            fontFamily: getThemeValue(theme, "fontFamily"),
            fontSize: "13px",
            fontWeight: 600,
          }}
        >
          <HeyAiSendIcon size={18} />
          {!useNeutralTextWindow && <span>{t.buttonSend}</span>}
        </button>
      </div>
    </form>
  );
};

export default ChatInput;
